export const MIN_GAMES_FOR_LUCK_RANKING = 5;

const SPECIAL_OUTCOME_PATTERN = /^spin\s+again\b/i;

function normalizeName(value) {
  return String(value || "").trim().toLowerCase();
}

function getDisplayName(participant) {
  return String(participant?.displayName || participant?.name || "").trim();
}

function getWinnerName(result) {
  return String(result?.participantName || "").trim();
}

function getGameId(game) {
  const candidate = [game?.id, game?.gameId, game?.ID].find(
    (value) => value !== undefined && value !== null && value !== "",
  );
  return candidate === undefined ? "" : String(candidate);
}

function isSpecialOutcome(name) {
  return SPECIAL_OUTCOME_PATTERN.test(name);
}

function getGameIdentity(game) {
  const id = getGameId(game);
  if (id) return `id:${id}`;

  const participants = Array.isArray(game?.participants)
    ? game.participants.map(getDisplayName).sort()
    : [];
  const results = Array.isArray(game?.results)
    ? game.results.map(getWinnerName).sort()
    : [];
  return `fallback:${JSON.stringify({
    title: game?.title || game?.name || "",
    date: game?.createdAt || game?.createdDate || game?.date || "",
    participants,
    results,
  })}`;
}

function getParticipantKey(participant) {
  const name = getDisplayName(participant);
  const normalizedName = normalizeName(name);
  if (normalizedName) return `name:${normalizedName}`;

  const id = participant?.id;
  return id === undefined || id === null || id === "" ? "" : `id:${id}`;
}

function getNonparticipantNames(nonparticipantNames) {
  const values = nonparticipantNames instanceof Set
    ? Array.from(nonparticipantNames)
    : Array.isArray(nonparticipantNames)
      ? nonparticipantNames
      : [];

  return new Set(
    values
      .map((name) => normalizeName(name))
      .filter(Boolean),
  );
}

function createParticipant(key, name) {
  return {
    key,
    name: name || "Unknown participant",
    gamesParticipated: 0,
    actualWins: 0,
    expectedWins: 0,
    variance: 0,
  };
}

export function calculateLuckStatistics(historyGames, nonparticipantNames = new Set()) {
  const games = Array.isArray(historyGames) ? historyGames : [];
  const excludedNames = getNonparticipantNames(nonparticipantNames);
  const participantsByKey = new Map();
  const seenGames = new Set();

  games.forEach((game) => {
    if (!game || typeof game !== "object") return;

    const gameIdentity = getGameIdentity(game);
    if (seenGames.has(gameIdentity)) return;
    seenGames.add(gameIdentity);

    const eligibleParticipants = new Map();
    const participants = Array.isArray(game.participants) ? game.participants : [];

    participants.forEach((participant) => {
      const name = getDisplayName(participant);
      const normalizedName = normalizeName(name);
      const key = getParticipantKey(participant);
      if (!key || !name || isSpecialOutcome(name) || excludedNames.has(normalizedName)) return;
      if (!eligibleParticipants.has(key)) eligibleParticipants.set(key, name);
    });

    const participantCount = eligibleParticipants.size;
    if (participantCount === 0) return;

    const gameWinnerKeys = new Set();
    const results = Array.isArray(game.results) ? game.results : [];

    results.forEach((result) => {
      const winnerName = getWinnerName(result);
      const normalizedWinnerName = normalizeName(winnerName);
      if (!winnerName || isSpecialOutcome(winnerName) || excludedNames.has(normalizedWinnerName)) return;

      const winnerKey = `name:${normalizedWinnerName}`;
      if (eligibleParticipants.has(winnerKey)) gameWinnerKeys.add(winnerKey);
    });

    if (participantCount === 1 && gameWinnerKeys.size === 0) {
      console.warn(
        `Skipping giveaway ${getGameId(game) || "(unknown id)"}: it has one eligible participant but no recorded eligible winner.`,
      );
      return;
    }

    const winnerCount = gameWinnerKeys.size;
    if (winnerCount > participantCount) {
      console.warn(
        `Skipping giveaway ${getGameId(game) || "(unknown id)"}: winner count (${winnerCount}) exceeds eligible participant count (${participantCount}).`,
      );
      return;
    }

    const probability = winnerCount / participantCount;
    const individualVariance = probability * (1 - probability);

    eligibleParticipants.forEach((name, key) => {
      if (!participantsByKey.has(key)) participantsByKey.set(key, createParticipant(key, name));
      const participant = participantsByKey.get(key);
      participant.gamesParticipated += 1;
      participant.expectedWins += probability;
      participant.variance += individualVariance;
    });

    gameWinnerKeys.forEach((winnerKey) => {
      participantsByKey.get(winnerKey).actualWins += 1;
    });
  });

  return Array.from(participantsByKey.values()).map((participant) => {
    const luckScore = participant.variance > 0
      ? (participant.actualWins - participant.expectedWins) / Math.sqrt(participant.variance)
      : 0;
    const safeLuckScore = Number.isFinite(luckScore) ? luckScore : 0;
    const safeExpectedWins = Number.isFinite(participant.expectedWins) ? participant.expectedWins : 0;
    return {
      ...participant,
      expectedWins: safeExpectedWins,
      variance: Number.isFinite(participant.variance) ? participant.variance : 0,
      luckScore: safeLuckScore,
      winDifference: participant.actualWins - safeExpectedWins,
    };
  });
}

export function getLuckRankings(participants, minimumGames = MIN_GAMES_FOR_LUCK_RANKING) {
  const qualified = (Array.isArray(participants) ? participants : [])
    .filter((participant) => participant.gamesParticipated >= minimumGames);
  const byName = (left, right) => left.name.localeCompare(right.name);

  const luckiest = [...qualified]
    .sort((left, right) => right.luckScore - left.luckScore || byName(left, right))
    .slice(0, 3);
  const unluckiest = [...qualified]
    .sort((left, right) => left.luckScore - right.luckScore || byName(left, right))
    .slice(0, 3);

  return { luckiest, unluckiest };
}
